import { Navigate, Outlet, useLocation } from 'react-router';
import { UniversalLoadingState } from '@afrilink/ui';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useStaffRole } from '@/shared/hooks/useStaffRole';

/**
 * Route guard for the sales console (`/sales/*`).
 *
 * - Anonymous → `/sign-in` (with `from` location preserved)
 * - Authenticated user with NO sales-relevant staff_profile role → `/app`
 * - Sales staff (sales_agent / sales_manager) → admitted
 * - Admin (Phase 11 staff_profile.role='admin') → admitted (cross-company view)
 *
 * Note: Phase 11 external sales staff have no `auth.users.raw_app_meta_data.role`,
 * so the existing `StaffRoute` would 403 them. That's why this is its own guard.
 */
export function SalesRoute() {
  const { loading: authLoading, isAuthenticated } = useAuth();
  const { loading: roleLoading, isSalesStaff, isAdmin } = useStaffRole();
  const location = useLocation();

  if (authLoading || roleLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-card-subtle">
        <UniversalLoadingState variant="spinner" caption="Loading…" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/sign-in"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  if (!isSalesStaff && !isAdmin) {
    return <Navigate to="/app" replace />;
  }

  return <Outlet />;
}
